import React, { useMemo } from 'react';
import type { CycleResult } from '~/utils/cycleCalculations';

interface CycleVisualizerProps {
  result: CycleResult;
}

export default function CycleVisualizer({ result }: CycleVisualizerProps) {
  const today = new Date();
  
  // 计算当前在周期中的位置
  const cycleInfo = useMemo(() => {
    const phases = [
      {
        name: '月经期',
        color: '#ef4444',
        start: new Date(result.nextPeriod.getTime() - 28 * 24 * 60 * 60 * 1000),
        end: new Date(result.nextPeriod.getTime() - 23 * 24 * 60 * 60 * 1000)
      },
      {
        name: '安全期',
        color: '#3b82f6',
        start: result.safeAfterStart,
        end: result.safeAfterEnd
      },
      {
        name: '易孕期',
        color: '#10b981',
        start: result.fertileStart,
        end: result.fertileEnd
      },
      {
        name: '排卵期',
        color: '#8b5cf6',
        start: result.ovulationStart,
        end: result.ovulationEnd
      },
      {
        name: '安全期',
        color: '#3b82f6',
        start: result.safeBeforeStart,
        end: result.safeBeforeEnd
      }
    ];

    // 找到当前所在的阶段
    const currentPhase = phases.find(phase => 
      today >= phase.start && today <= phase.end
    ) || { name: '未知', color: '#6b7280' };

    return { phases, currentPhase };
  }, [result, today]);

  // 计算圆形进度
  const getCircleProgress = () => {
    const cycleStart = new Date(result.nextPeriod.getTime() - 28 * 24 * 60 * 60 * 1000);
    const totalDays = 28;
    const daysPassed = Math.floor((today.getTime() - cycleStart.getTime()) / (1000 * 60 * 60 * 24));
    const progress = Math.min(Math.max(daysPassed / totalDays, 0), 1);
    return progress * 360;
  };

  const circleProgress = getCircleProgress();

  return (
    <div className="flex flex-col items-center space-y-6">
      {/* 圆形周期图 */}
      <div className="relative">
        <svg width="200" height="200" className="transform -rotate-90">
          {/* 背景圆环 */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="8"
          />
          
          {/* 月经期 (0-5天, 0-64.3度) */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#ef4444"
            strokeWidth="8"
            strokeDasharray={`${(5/28) * 502.4} ${502.4 - (5/28) * 502.4}`}
            strokeDashoffset="0"
            opacity="0.8"
          />
          
          {/* 安全期1 (6-10天) */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="8"
            strokeDasharray={`${(5/28) * 502.4} ${502.4 - (5/28) * 502.4}`}
            strokeDashoffset={`-${(5/28) * 502.4}`}
            opacity="0.8"
          />
          
          {/* 易孕期 (11-17天) */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#10b981"
            strokeWidth="8"
            strokeDasharray={`${(7/28) * 502.4} ${502.4 - (7/28) * 502.4}`}
            strokeDashoffset={`-${(10/28) * 502.4}`}
            opacity="0.8"
          />
          
          {/* 排卵期 (14-15天，在易孕期中突出显示) */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#8b5cf6"
            strokeWidth="12"
            strokeDasharray={`${(2/28) * 502.4} ${502.4 - (2/28) * 502.4}`}
            strokeDashoffset={`-${(13/28) * 502.4}`}
            opacity="1"
          />
          
          {/* 安全期2 (18-28天) */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="8"
            strokeDasharray={`${(11/28) * 502.4} ${502.4 - (11/28) * 502.4}`}
            strokeDashoffset={`-${(17/28) * 502.4}`}
            opacity="0.8"
          />
          
          {/* 当前位置指示器 */}
          <circle
            cx={100 + 80 * Math.cos((circleProgress - 90) * Math.PI / 180)}
            cy={100 + 80 * Math.sin((circleProgress - 90) * Math.PI / 180)}
            r="6"
            fill="#1f2937"
            stroke="white"
            strokeWidth="2"
          />
        </svg>
        
        {/* 中心信息 */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center bg-white/90 backdrop-blur-sm rounded-full w-32 h-32 flex flex-col items-center justify-center">
            <div className="text-2xl mb-1">📅</div>
            <div className="text-sm font-medium text-gray-800">
              {cycleInfo.currentPhase.name}
            </div>
            <div 
              className="w-4 h-4 rounded-full mt-1"
              style={{ backgroundColor: cycleInfo.currentPhase.color }}
            ></div>
          </div>
        </div>
      </div>

      {/* 图例 */}
      <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-red-500"></div>
          <span className="text-sm text-gray-700">月经期</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-blue-500"></div>
          <span className="text-sm text-gray-700">安全期</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-green-500"></div>
          <span className="text-sm text-gray-700">易孕期</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-purple-500"></div>
          <span className="text-sm text-gray-700">排卵期</span>
        </div>
      </div>

      {/* 状态指示器 */}
      <div className="flex flex-wrap gap-2 justify-center">
        <span className={`status-indicator ${
          cycleInfo.currentPhase.name === '月经期' ? 'status-menstrual' :
          cycleInfo.currentPhase.name === '排卵期' ? 'status-ovulation' :
          cycleInfo.currentPhase.name === '易孕期' ? 'status-fertile' :
          'status-safe'
        }`}>
          当前：{cycleInfo.currentPhase.name}
        </span>
      </div>

      {/* 时间轴 */}
      <div className="w-full max-w-md">
        <h4 className="text-sm font-medium text-gray-700 mb-3 text-center">本周期时间轴</h4>
        <div className="space-y-2">
          {[
            { name: '月经期', date: new Date(result.nextPeriod.getTime() - 28 * 24 * 60 * 60 * 1000), color: 'bg-red-500' },
            { name: '安全期开始', date: result.safeAfterStart, color: 'bg-blue-500' },
            { name: '易孕期开始', date: result.fertileStart, color: 'bg-green-500' },
            { name: '排卵期', date: result.ovulationStart, color: 'bg-purple-500' },
            { name: '安全期开始', date: result.safeBeforeStart, color: 'bg-blue-500' },
            { name: '下次月经', date: result.nextPeriod, color: 'bg-red-500' }
          ].map((item, index) => {
            const isPast = today > item.date;
            const isToday = today.toDateString() === item.date.toDateString();
            
            return (
              <div key={index} className={`flex items-center space-x-3 p-2 rounded-lg ${
                isToday ? 'bg-yellow-50 border border-yellow-200' : 
                isPast ? 'opacity-60' : ''
              }`}>
                <div className={`w-3 h-3 rounded-full ${item.color} ${isPast ? 'opacity-60' : ''}`}></div>
                <div className="flex-1">
                  <div className={`text-sm ${isToday ? 'font-bold text-yellow-800' : 'text-gray-700'}`}>
                    {item.name}
                  </div>
                  <div className={`text-xs ${isToday ? 'text-yellow-600' : 'text-gray-500'}`}>
                    {item.date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                    {isToday && ' (今天)'}
                  </div>
                </div>
                {isToday && (
                  <div className="text-yellow-600">
                    <span className="text-lg">👈</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}